import React from 'react';
import { Info, Lightbulb, AlertTriangle, CheckCircle, Search, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

export type CalloutType = 'note' | 'tip' | 'warning' | 'best-practice' | 'deep-dive' | 'important';

interface CalloutProps {
  type?: CalloutType;
  title?: string;
  children: React.ReactNode;
}

const config = {
  note: {
    icon: Info,
    styles: 'bg-blue-500/10 border-blue-500/20 text-blue-400',
    iconColor: 'text-blue-500',
    defaultTitle: 'Note',
  },
  tip: {
    icon: Lightbulb,
    styles: 'bg-green-500/10 border-green-500/20 text-green-400',
    iconColor: 'text-green-500',
    defaultTitle: 'Tip',
  },
  warning: {
    icon: AlertTriangle,
    styles: 'bg-yellow-500/10 border-yellow-500/20 text-yellow-400',
    iconColor: 'text-yellow-500',
    defaultTitle: 'Warning',
  },
  'best-practice': {
    icon: CheckCircle,
    styles: 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400',
    iconColor: 'text-emerald-500',
    defaultTitle: 'Best Practice',
  },
  'deep-dive': {
    icon: Search,
    styles: 'bg-purple-500/10 border-purple-500/20 text-purple-400',
    iconColor: 'text-purple-500',
    defaultTitle: 'Deep Dive',
  },
  important: {
    icon: AlertCircle,
    styles: 'bg-red-500/10 border-red-500/20 text-red-400',
    iconColor: 'text-red-500',
    defaultTitle: 'Important',
  },
};

const Callout: React.FC<CalloutProps> = ({ type = 'note', title, children }) => {
  const currentConfig = config[type] || config.note;
  const Icon = currentConfig.icon;

  return (
    <div className={cn("my-6 flex gap-3 rounded-lg border p-4", currentConfig.styles)}>
      <div className="mt-0.5 shrink-0">
        <Icon className={cn("h-5 w-5", currentConfig.iconColor)} />
      </div>
      <div className="flex-1 w-full prose-callout">
        <strong className={cn("block font-semibold mb-1 text-base tracking-wide", currentConfig.iconColor)}>
          {title || currentConfig.defaultTitle}
        </strong>
        <div className="text-sm opacity-90 leading-relaxed text-foreground">
          {children}
        </div>
      </div>
    </div>
  );
};

export default Callout;
